import { useEffect, useState } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { type Servicepoint, useFetchMultipleServicepoints, useFetchServicepoints } from '@dhl/receiver-servicepoint'
import type { ServicepointInputValue } from '@dhl/servicepoint-input'
import {
  type AddressInterventionPreference,
  type AgreedPlaceInterventionPreference,
  type AppError,
  type CombinedPreferences,
  type ServicePointInterventionPreference,
  useGetPreferenceOptions,
  useGetInterventionPreferences,
  useGetAgreedPlacePicture,
} from '@dhl/receiver-preference-api'
import {
  type AgreedPlaceInterventionPreferenceWithPhoto,
  type AvailablePreferences,
  type PreferenceFormValues,
  getEmptyPreference,
} from './model.js'

import { useGetProfileAddressById } from '../profile/hooks.js'

type ReturnValue = {
  data: {
    initialValues: PreferenceFormValues
    preference?: AddressInterventionPreference
    servicepoints: Servicepoint[] | null
  }
  isLoading: boolean
  isLoadingPreferences: boolean
}

const preferenceByAddressIdSelector = (addressId: string) => (data: CombinedPreferences) =>
  data.preferences?.find((p) => p.addressId === addressId) as unknown as AddressInterventionPreference

export const useGetInterventionPreferencesByAddressId = (
  addressId: string
): UseQueryResult<AddressInterventionPreference, AppError | null> =>
  useGetInterventionPreferences({
    query: {
      select: preferenceByAddressIdSelector(addressId),
    },
  })

export const usePreferenceFormValues = (addressId: string): ReturnValue => {
  const { data: allPreferences, isLoading: isLoadingPreferences } = useGetInterventionPreferences()
  const { data: profileAddress, isLoading: isLoadingProfile } = useGetProfileAddressById(addressId)

  const [preference, setPreference] = useState<AddressInterventionPreference | undefined>()
  const servicePointPreference = preference?.servicePoint as unknown as ServicePointInterventionPreference
  const agreedPlacePreference = preference?.agreedPlace as unknown as AgreedPlaceInterventionPreference

  const { data: agreedPlacePhoto, isFetching: isFetchingPhoto } = useGetAgreedPlacePicture(addressId)

  const { data: preferencesOptionsFallback, isInitialLoading: isInitialLoadingFallback } = useGetPreferenceOptions(
    profileAddress?.address.countryCode ?? '',
    profileAddress?.address.postalCode ?? '',
    {
      query: {
        enabled: !isLoadingPreferences && !!profileAddress && !preference,
      },
    }
  )

  // If a profile address is supplied, but this is not returned by the preference API,
  // it could be that the address is not yet synced with the preference API.
  // In that case, we fetch the preference options for that address in a separate call.
  // This only happen when the user was redirected after adding a new address.
  useEffect(() => {
    let pref: AddressInterventionPreference | undefined

    if (!isLoadingPreferences && allPreferences) {
      pref = preferenceByAddressIdSelector(addressId)(allPreferences)

      if (pref) {
        setPreference(pref)
        return
      }
    }

    if (!pref && !isInitialLoadingFallback && profileAddress && preferencesOptionsFallback) {
      setPreference(getEmptyPreference(profileAddress, preferencesOptionsFallback))
    }
  }, [
    addressId,
    allPreferences,
    isInitialLoadingFallback,
    isLoadingPreferences,
    preferencesOptionsFallback,
    profileAddress,
  ])

  const isNewPreferenceOptions = !!servicePointPreference?.availableServicePointIds?.length

  const { data: queryServicepoints = [], isLoading: isLoadingQueryServicepoints } = useFetchServicepoints(
    {
      country: 'NL',
      postalCode: preference?.address.postalCode,
      sameDepot: true,
      limit: 10,
    },
    {
      query: {
        enabled: !isNewPreferenceOptions,
      },
    }
  )

  const { data: multiServicepoints = [], isLoading: isLoadingMultiServicepoints } = useFetchMultipleServicepoints(
    {
      countryCode: 'NL',
      postalCode: preference?.address.postalCode ?? '',
      servicePointIds: servicePointPreference?.availableServicePointIds || [],
    },
    {
      query: {
        enabled: isNewPreferenceOptions,
      },
    }
  )

  const servicepoints = isNewPreferenceOptions ? multiServicepoints : queryServicepoints
  const isLoadingServicepoints = isNewPreferenceOptions ? isLoadingMultiServicepoints : isLoadingQueryServicepoints

  const initialServicePoint = (): ServicepointInputValue | null => {
    const servicepoint = servicepoints.find((sp) => sp.servicepointKey === servicePointPreference?.psfKey)

    if (servicepoint) {
      return {
        inputKind: 'Servicepoint',
        servicepoint,
      }
    }
    return null
  }

  const initialPreference = (): AvailablePreferences | null => {
    if (preference?.servicePoint?.active) {
      return 'servicePoint'
    }

    if (preference?.agreedPlace?.active && !preference?.agreedPlace?.ringDoorbell) {
      return 'agreedPlace'
    }

    const shouldRingDoorbellWithNeighbours =
      !preference?.agreedPlace?.active && preference?.agreedPlace?.ringDoorbell !== null
    const shouldRingDoorbellWithAgreedPlace = preference?.agreedPlace?.active && preference?.agreedPlace?.ringDoorbell

    if (shouldRingDoorbellWithNeighbours || shouldRingDoorbellWithAgreedPlace) {
      return 'ringDoorbell'
    }

    return null
  }

  const initialAgreedPlace = (): AgreedPlaceInterventionPreferenceWithPhoto | null => {
    if (
      !preference?.agreedPlace ||
      (!agreedPlacePreference?.description && agreedPlacePreference?.ringDoorbell === null)
    ) {
      return null
    }

    return {
      agreedPlacePhoto,
      description: agreedPlacePreference?.description,
      active: agreedPlacePreference?.active,
      available: agreedPlacePreference?.available || false,
      ringDoorbell: agreedPlacePreference?.ringDoorbell ?? false,
    }
  }

  const initialValues: PreferenceFormValues = {
    addressId,
    ringDoorbell: null,
    preference: initialPreference(),
    agreedPlace: initialAgreedPlace(),
    servicePoint: initialServicePoint(),
  }

  const isLoadingPreferenceData = isInitialLoadingFallback || isLoadingPreferences || isLoadingProfile
  const isLoading = isLoadingPreferenceData || isLoadingServicepoints || isFetchingPhoto

  return {
    data: {
      initialValues,
      preference: isLoadingPreferenceData ? undefined : preference,
      servicepoints,
    },
    isLoading,
    isLoadingPreferences: isLoadingPreferenceData,
  }
}
